<template>
  <form name="notification-pages-form" class="NotificationPagesForm" @submit.prevent="$emit('save')">
    <PassportTemplatesMain
      :form="form"
      :data="data"
      :disabled="isView"
      :skeleton="skeleton"
      :is-create="isCreate"
      :is-view="isView"
      class="my-4"
    />

    <PassportTemplatesSheet
      v-for="(item, index) in form.sheets"
      :key="item.uuid"
      :form="item"
      :field-name="validateSheetFieldName(item)"
      :disabled="isView"
      :skeleton="skeleton"
      class="my-4 js-sheet"
      :upable="index > 0"
      :downable="form.sheets.length > 1 && form.sheets.length - 1 !== index"
      deleteable
      @sortable-positions-up="sortSheet(item, index, index - 1)"
      @sortable-positions-down="sortSheet(item, index, index + 1)"
      @delete="deleteSheet(item, index)"
    />

    <button type="submit" class="d-none" />
  </form>
</template>

<script>
import BLayoutForm from '@/components/layouts/BLayoutForm.vue'
import PassportTemplatesMain from '@/views/PassportTemplates/components/PassportTemplatesMain.vue'
import PassportTemplatesSheet, { createDefaultData as createDefaultDataSheet } from '@/views/PassportTemplates/components/Fields/PassportTemplatesSheet.vue'

import { PassportTemplateType } from '@/typings/api/Passport'
import { cloneDeep } from 'lodash'
import { getUuid } from '@/views/FormTemplates/utils'
import { createModalRemove } from '@/helpers/createModalRemove'


const createDefaultData = () => ({
  name: '',
  ate_type: PassportTemplateType.SCHOOL,
  sheets: [],
})

export default {
  name: 'PassportTemplatesForm',
  inject: ['$validator'],
  provide() {
    return this.copy
  },
  components: {
    PassportTemplatesMain,
    PassportTemplatesSheet,
  },
  extends: BLayoutForm,
  props: {
    editable: Boolean,
    skeleton: Boolean,
    loading: Boolean,
    data: {
      type: Object,
      required: true,
      default: createDefaultData,
    },
  },
  data: (vm) => ({
    copy: {
      sheetTree: vm.sheetTree,
    },
  }),
  computed: {
    sheetTree() {
      const { form } = this
      if (!form.sheets) {
        return {
          $children: null,
          $all_cells: null,
          name: form.name,
        }
      }
      const $childrenSheets = form.sheets.map((sheet) => {
        const data = {
          $parent: form,
          $children: [],
          $data: sheet,
          uuid: sheet.uuid,
          name: sheet.name,
          code: sheet.code,
        }

        const $childrenCells = sheet.cells.map((cell) => ({
          $parent: data,
          $children: [],
          $data: cell,
          uuid: cell.uuid,
          name: cell.name,
          code: cell.code,
        }))

        data.$children = $childrenCells

        return data
      })

      const allCells = $childrenSheets.map((_) => _.$children).flat()

      return {
        $children: $childrenSheets,
        $all_cells: allCells,
        name: form.name,
      }
    },
  },
  watch: {
    sheetTree: {
      immediate: true,
      handler(val) {
        const { sheetTree } = this.copy
        if (!sheetTree) {
          this.copy.sheetTree = val
          return
        }

        Object.entries(val).forEach(([key, value]) => {
          // Нужно для реативности !!!
          sheetTree[key] = value
        })
      },
    },
  },
  methods: {
    onBeforeSubmit() {
      if (this.form.sheets?.length < 1) {
        this.$validator.errors.add({
          field: this.name,
          msg: 'Необходимо добавить листы.',
        })
        return false
      }
      // eslint-disable-next-line no-restricted-syntax
      for (const sheet of this.form.sheets) {
        if (sheet.cells?.length === 0) {
          this.$validator.errors.add({
            field: this.validateSheetFieldName(sheet),
            msg: 'Необходимо добавить ячейки.',
          })
          return false
        }
      }

      return true
    },
    async onSubmit() {
      if (!this.onBeforeSubmit()) {
        return null
      }
      const result = await this.$validator.validate()

      if (!result) {
        this.$notice.validatorFormError(this)
        const message = `Error submit: ${this.$options.name}`
        const error = new Error(message)
        return Promise.reject(error)
      }

      const { form } = this

      const data = {
        ...form,

        fields: form.sheets,
        sheets: void 0,
      }

      this.$emit('submit', data)

      return data
    },
    onReset(data = this.data) {
      this.form = cloneDeep(data)

      // Преобразование fields в sheets, иначе компоненты от ФСД не будут работать.
      this.$set(this.form, 'sheets', this.form.fields)
      delete this.form.fields

      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
    validateSheetFieldName(sheet) {
      return `sheet-${sheet.uuid}`
    },
    async addSheet() {
      const list = this.form.sheets
      const uuid = await getUuid()
      const uuid2 = await getUuid()
      let code = 0
      if (list.length) {
        const cleanedArray = list.map((item) => item.code.replace(/\D+/g, ''))
        code = Math.max(...cleanedArray.map((item) => parseInt(item, 10)))
      }
      const data = createDefaultDataSheet(+code + 1, uuid, uuid2)
      list.push(data)

      this.$validator.errors.remove(this.name)

      this.$nextTick(this.scrollToBottom)
    },
    sortSheet(data, oldIndex, newIndex) {
      const list = this.form.sheets
      list.splice(oldIndex, 1, list[newIndex])
      list.splice(newIndex, 1, data)
    },
    async deleteSheet(data, index) {
      const propsData = {
        titleText: `Удалить лист "${data.name}" <small>(${data.code})</small>`,
      }

      const modal = await createModalRemove(this, propsData)
      const result = await modal.open()
      if (!result) return
      const list = this.form.sheets
      list.splice(index, 1)
    },
  },
}
</script>
